import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    Plus,
    Clock,
    AlertCircle,
    History,
    User,
    Send,
    HelpCircle,
    Camera
} from 'lucide-react';
import { cn } from '../lib/utils';
import { StatusBadge } from '../components/ui/StatusBadge';

const SupportTickets: React.FC<{ activeUnitId: string }> = ({ activeUnitId }) => {
    const [tickets, setTickets] = useState<any[]>([]);
    const [isRaising, setIsRaising] = useState(false);
    const [desc, setDesc] = useState('');
    const [category, setCategory] = useState('General');
    const [files, setFiles] = useState<FileList | null>(null);
    const [comments, setComments] = useState<Record<string, string>>({});

    useEffect(() => {
        if (activeUnitId) fetchTickets();
    }, [activeUnitId]);

    const fetchTickets = async () => {
        const res = await api.get('/tickets');
        setTickets(res.data.filter((t: any) => t.unit_id === activeUnitId));
    };

    const handleUploadPhotos = async (ticketId: string, files: FileList) => {
        const formData = new FormData();
        Array.from(files).forEach(f => formData.append('photos', f));
        await api.post(`/tickets/${ticketId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await api.post('/tickets', {
                description: desc,
                category,
                unit_id: activeUnitId
            });
            if (files && files.length > 0) {
                await handleUploadPhotos(res.data.id, files);
            }
            setDesc(''); setCategory('General'); setFiles(null);
            setIsRaising(false);
            fetchTickets();
            alert('Ticket raised successfully.');
        } catch (e) { alert('Submission failed'); }
    };

    const handleAddComment = async (ticketId: string) => {
        const message = comments[ticketId];
        if (!message) return;
        try {
            await api.post(`/tickets/${ticketId}/logs`, { message });
            setComments({ ...comments, [ticketId]: '' });
            fetchTickets();
        } catch (e) { alert('Comment failed'); }
    };

    return (
        <div className="space-y-8 max-w-3xl mx-auto pb-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Support Desk</h1>
                    <p className="text-slate-500 mt-1">Maintenance requests and complaints</p>
                </div>
                <button
                    onClick={() => setIsRaising(!isRaising)}
                    className="bg-red-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-red-700 transition-all flex items-center gap-2 shadow-lg shadow-red-100"
                >
                    {isRaising ? 'Cancel' : 'Raise Ticket'}
                    <Plus size={18} className={cn("transition-transform", isRaising && "rotate-45")} />
                </button>
            </div>

            {isRaising && (
                <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-6 animate-in slide-in-from-top-2">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Category</label>
                                <select value={category} onChange={e => setCategory(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm">
                                    <option>General</option>
                                    <option>Maintenance</option>
                                    <option>Security</option>
                                    <option>Cleanliness</option>
                                    <option>Plumbing</option>
                                    <option>Electrical</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Photos (Optional)</label>
                                <input type="file" multiple onChange={e => setFiles(e.target.files)} className="w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase">Description</label>
                            <textarea
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                                placeholder="Describe the issue..."
                                rows={4}
                                value={desc}
                                onChange={e => setDesc(e.target.value)}
                                required
                            />
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-slate-900 text-white px-8 py-2.5 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all">Submit Ticket</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="space-y-6">
                {tickets.map((t: any) => (
                    <div key={t.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:border-violet-200 transition-all">
                        <div className="p-6 border-b border-slate-100">
                            <div className="flex justify-between items-start gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded border border-slate-100 uppercase">#{t.id.slice(0,8)}</span>
                                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1">
                                            <Clock size={12} /> {new Date(t.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <h3 className="font-bold text-slate-900">{t.description}</h3>
                                    <p className="text-[10px] font-bold text-violet-600 uppercase mt-1">{t.category}</p>
                                </div>
                                <StatusBadge status={t.status} />
                            </div>
                        </div>

                        <div className="p-6 bg-slate-50/30">
                            <div className="space-y-4 mb-6">
                                {t.logs?.map((log: any) => (
                                    <div key={log.id} className="flex gap-3">
                                        <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                                            <User size={14} className="text-slate-400" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                                                <div className="flex justify-between mb-1">
                                                    <span className="text-[10px] font-bold text-slate-500 uppercase">Update</span>
                                                    <span className="text-[9px] text-slate-400">{new Date(log.created_at).toLocaleString()}</span>
                                                </div>
                                                <p className="text-sm text-slate-600 leading-relaxed">{log.message}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="relative">
                                <input
                                    className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-4 pr-12 text-sm focus:ring-2 focus:ring-violet-500 outline-none shadow-sm"
                                    placeholder="Add a reply..."
                                    value={comments[t.id] || ''}
                                    onChange={e => setComments({ ...comments, [t.id]: e.target.value })}
                                />
                                <button
                                    onClick={() => handleAddComment(t.id)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-all"
                                >
                                    <Send size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {tickets.length === 0 && (
                    <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                        <HelpCircle size={40} className="text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">No active support requests</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupportTickets;
